import MojitoPresentation from 'mojito/presentation';
import MojitoServices from 'mojito/services';
import { isArray } from 'mojito/utils';

const { utils: EventUtils } = MojitoServices.SportsContent.Events;
const BetsTypes = MojitoServices.Bets.types;
const BetsUtils = MojitoServices.Bets.utils;
const { CASHOUT_STATUS } = BetsTypes;
const { UNAVAILABLE, INACTIVE } = BetsTypes.CASHOUT_AVAILABILITY;
const { IntentActionTrigger } = MojitoPresentation.Base.Intent;
const SportUtils = MojitoServices.SportsContent.Sports.utils;

/**
 * A utility class specifically designed for use within the YourBets module.
 *
 * @name utils
 * @class YourBetsUtils
 * @memberof Mojito.Modules.YourBets
 */

/**
 * Evaluates if the given bet leg pertains to an event that is ongoing and not outright,
 * or if the sport id of the leg is not racing and it has already started.
 *
 * @param {Mojito.Services.Bets.Types.Leg} leg - The leg of the bet.
 * @param {object} event - The event object.
 *
 * @returns {boolean} Returns true if the bet leg is considered "live", otherwise false.
 * @function Mojito.Modules.YourBets.utils.isLegLive
 */
export function isLegLive(leg, event) {
    if (event) {
        return event.details.inPlay && !EventUtils.isOutrightEventType(event.eventType);
    }

    // If we don't have the event data for the given leg we guess!
    // 1. Racing? Does not have scores/current standings = Not live in the open-bets sense of the word
    // 2. Has the starting time passed?
    const { sportId, eventStartTime } = leg.legInfo;
    return !SportUtils.isRacingSport(sportId) && new Date(eventStartTime) < new Date();
}

/**
 * Determines whether the cashout option should be displayed for a given bet based on its status,
 * configuration, and other related data.
 *
 * @param {object} info - The cashout information object containing availability and auto-cashout details.
 * @param {boolean} isDisabled - A flag indicating whether the cashout feature is globally disabled.
 * @param {string} status - The current status of the cashout feature. Usually corresponds to a value from `CASHOUT_STATUS`.
 * @param {object} config - Configuration settings that influence cashout visibility.
 *
 * @returns {boolean} Returns `true` if the cashout option should be displayed, otherwise `false`.
 * @function Mojito.Modules.YourBets.utils.shouldShowCashout
 */
export function shouldShowCashout(info, isDisabled, status, config) {
    if (isDisabled || !info || status === CASHOUT_STATUS.OFFER_FAILED) {
        return false;
    }
    const { showUnavailableCashoutStatus, showInactiveCashoutStatus } = config;
    const autoCashoutAmount = info?.autoCashout?.triggerAmount || 0;

    if (autoCashoutAmount > 0 || status === CASHOUT_STATUS.FAILED) {
        return true;
    } else if (info.availability === UNAVAILABLE) {
        return showUnavailableCashoutStatus;
    } else if (info.availability === INACTIVE) {
        return showInactiveCashoutStatus;
    }
    return true;
}

/**
 * Sanitizes the tabs configuration by filtering out any falsy values and converting to BETS_TYPE enum values.
 *
 * @param {Array<string>} tabs - Tabs list config.
 *
 * @returns {Array<Mojito.Modules.YourBets.types.BETS_TYPE>} List of sanitized tabs names according to enum.
 * @function Mojito.Modules.YourBets.utils.sanitizeTabsConfig
 */
export const sanitizeTabsConfig = tabs => {
    return isArray(tabs) ? tabs.filter(Boolean).map(tabName => tabName.toUpperCase()) : [];
};

/**
 * Get live legs from a bet based on the event data.
 *
 * @param {Mojito.Services.Bets.types.Bet} bet - Bet.
 * @param {Array<object>} events - List of event objects.
 *
 * @returns {Array<Mojito.Services.Bets.types.Leg>} List of live legs from the bet.
 * @function Mojito.Modules.YourBets.utils.getLiveLegs
 */
export const getLiveLegs = (bet, events) =>
    (bet.legs || []).filter(leg => {
        const event = events?.find(event => event.id === leg.legInfo.eventId);
        if (isLegLive(leg, event)) {
            return leg;
        }
    });

/**
 * Navigates to the virtuals sport page or virtuals event page based on the fact if event is ended or not.
 *
 * @param {object} event - Event content object.
 *
 * @function Mojito.Modules.YourBets.utils.navigateToVirtuals
 */
export const navigateToVirtuals = event => {
    const { sportId, classId, id, ended } = event;
    if (ended) {
        IntentActionTrigger.showVirtualSportPage(sportId, classId);
    } else {
        IntentActionTrigger.showVirtualEventPage(sportId, classId, id);
    }
};

/**
 * Navigates to the meetings races page or to the future race event page if event is future race.
 *
 * @param {object} event - Event content object.
 *
 * @function Mojito.Modules.YourBets.utils.navigateToRacing
 */
export const navigateToRacing = event => {
    const { sportId, id, details } = event;
    const { meetingId, isFutureRace } = details;
    if (isFutureRace) {
        IntentActionTrigger.showFutureRaceEventPage(sportId, id);
    } else {
        IntentActionTrigger.showMeetingsRaces(sportId, meetingId, id);
    }
};

/**
 * Determines if a leg is an SGP+ Match Acca leg.
 *
 * @param {Mojito.Services.Bets.types.Bet} bet - Bet object.
 * @param {Mojito.Services.Bets.Types.Leg} leg - Bet leg.
 *
 * @returns {boolean} True when the bet is BB Acca and the leg is Match Acca.
 */
export const isSgpPlusMatchAccaLeg = (bet, leg) => !!bet?.isBBAcca && BetsUtils.isMatchAccaLeg(leg);
